// Tenantra Inc. - Public "Contact us" lead form for ads (full name, email, phone).
import { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2, Mail } from 'lucide-react';
import { Logo } from '@/components/Logo';
import api from '@/services/api';

const errText = (err) => { const d = err?.response?.data?.detail; return typeof d === 'string' ? d : Array.isArray(d) ? d.map(x => x?.msg).join(' ') : 'Something went wrong. Please try again.'; };

export default function ContactPage() {
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.full_name.trim().length >= 2 && /\S+@\S+\.\S+/.test(form.email) && form.phone.trim().length >= 7;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) { setError('Please enter your name, a valid email, and phone number.'); return; }
    setBusy(true); setError('');
    try {
      await api.post('/contact/lead', { full_name: form.full_name, email: form.email, phone: form.phone, message: form.message || undefined });
      setDone(true); window.scrollTo(0, 0);
    } catch (err) { setError(errText(err)); } finally { setBusy(false); }
  };

  const shell = (children) => (
    <div className="apply-type min-h-screen bg-white">
      <header className="bg-white/90 backdrop-blur border-b border-stone-200 sticky top-0 z-20">
        <div className="max-w-xl mx-auto px-5 h-14 flex items-center"><Logo /></div>
      </header>
      <main className="max-w-xl mx-auto px-5 py-12 sm:py-16">{children}</main>
    </div>
  );

  if (done) return shell(
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-stone-200 rounded-2xl shadow-sm p-10 text-center" data-testid="contact-success">
      <div className="w-14 h-14 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="w-7 h-7 text-teal-600" /></div>
      <h1 className="text-2xl font-bold tracking-tight text-stone-900">Thanks, we got it</h1>
      <p className="text-[15px] leading-relaxed text-stone-600 mt-4 max-w-sm mx-auto">Our team will reach out to you shortly at the email and phone number you provided.</p>
    </motion.div>
  );

  const field = (label, key, type, placeholder) => (
    <div>
      <label className="block text-sm font-medium text-stone-900 mb-1.5">{label}</label>
      <input
        type={type} value={form[key]} onChange={set(key)} placeholder={placeholder}
        className="w-full h-11 px-3 bg-white border border-stone-300 rounded-md text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
        data-testid={`contact-${key}`}
      />
    </div>
  );

  return shell(
    <div data-testid="contact-page">
      <p className="inline-flex items-center gap-1.5 text-[13px] font-medium text-teal-700 bg-teal-50 border border-teal-100 rounded-full px-3 py-1"><Mail className="w-3.5 h-3.5" /> Get in touch</p>
      <h1 className="mt-4 text-[1.9rem] font-bold tracking-tight text-stone-900 leading-tight">Contact us</h1>
      <p className="text-base text-stone-700 mt-2 mb-7">Leave your details and our team will get back to you shortly.</p>

      <form onSubmit={submit} className="space-y-4">
        {field('Full name', 'full_name', 'text', 'Jane Doe')}
        {field('Email', 'email', 'email', 'jane@example.com')}
        {field('Phone number', 'phone', 'tel', '(204) 555-0123')}
        <div>
          <label className="block text-sm font-medium text-stone-900 mb-1.5">Message <span className="text-stone-400 font-normal">(optional)</span></label>
          <textarea value={form.message} onChange={set('message')} rows={3} placeholder="How can we help?"
            className="w-full px-3 py-2.5 bg-white border border-stone-300 rounded-md text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 resize-none" data-testid="contact-message" />
        </div>
        {error && <p className="text-sm text-red-600" data-testid="contact-error">{error}</p>}
        <button type="submit" disabled={busy || !valid} className="w-full h-12 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-[15px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.99] transition-all shadow-md shadow-teal-600/10" data-testid="contact-submit">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}{busy ? 'Sending…' : 'Submit'}
        </button>
      </form>
    </div>
  );
}
